import React, { useState, useEffect } from 'react';
import { complianceService } from '../../../services/api';
import { getAppMarket } from '../../../services/MarketService';
import PRSWizard from './PRSWizard';

interface EditPropertyModalProps {
    isOpen: boolean;
    onClose: () => void;
    property: any;
    onSuccess: () => void;
}

const EditPropertyModal: React.FC<EditPropertyModalProps> = ({ isOpen, onClose, property, onSuccess }) => {
    const [line1, setLine1] = useState('');
    const [postcode, setPostcode] = useState('');
    const [type, setType] = useState('Residential');
    const [bedrooms, setBedrooms] = useState<number>(0);
    const [rent, setRent] = useState<number>(0);
    const [hasGasInstallation, setHasGasInstallation] = useState(true);
    const [prsRegNumber, setPrsRegNumber] = useState('');
    const [prsRegDate, setPrsRegDate] = useState('');
    const [prsExpiryDate, setPrsExpiryDate] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // F-307: Texas market branding (PRS -> Texas Rental Registration, £ -> $)
    const isTexas = getAppMarket() === 'texas';
    // F-307: Texas Registration wizard reachable from the edit modal.
    const [showRegWizard, setShowRegWizard] = useState(false);

    useEffect(() => {
        if (property && isOpen) {
            setLine1(typeof property.address === 'object' ? property.address.line1 : (property.address || ''));
            setPostcode(typeof property.address === 'object' ? property.address.postcode : (property.postcode || ''));
            setType(property.type || property.propertyType || 'Residential');
            setBedrooms(property.bedrooms || 0);
            setRent(property.rent || property.rentAmount || 0);
            setHasGasInstallation(property.hasGasInstallation !== false);
            setPrsRegNumber(property.prsRegistrationNumber || '');
            setPrsRegDate(property.prsRegistrationDate || '');
            setPrsExpiryDate(property.prsExpiryDate || '');
            setError(null);
        }
    }, [property, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        try {
            await complianceService.updateProperty(property.id || property._id, {
                address: { line1, postcode },
                postcode,
                type,
                bedrooms,
                rent,
                hasGasInstallation,
                prsRegistrationNumber: prsRegNumber,
                prsRegistrationDate: prsRegDate,
                prsExpiryDate
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.message || 'Failed to update property details.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Edit Property Details
                            </h3>
                        </div>
                    </div>
                    <form onSubmit={handleSubmit} className="mt-5 sm:mt-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 p-3 text-sm text-red-700 rounded border border-red-200">
                                {error}
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
                            <input
                                type="text"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                value={line1}
                                onChange={e => setLine1(e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{isTexas ? 'ZIP Code' : 'Postcode'}</label>
                            <input
                                type="text"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                value={postcode}
                                onChange={e => setPostcode(e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Category</label>
                            <select
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                value={type}
                                onChange={e => setType(e.target.value)}
                            >
                                <option value="Residential">Residential</option>
                                <option value="HMO">HMO</option>
                                <option value="Commercial">Commercial</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Bedrooms</label>
                                <input
                                    type="number"
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                    value={bedrooms}
                                    onChange={e => setBedrooms(parseInt(e.target.value) || 0)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Rent / Month ({isTexas ? '$' : '£'})</label>
                                <input
                                    type="number"
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                    value={rent}
                                    onChange={e => setRent(parseFloat(e.target.value) || 0)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Gas Installation Exception */}
                        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100 mt-4">
                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        id="editHasGasInstallation"
                                        type="checkbox"
                                        checked={hasGasInstallation}
                                        onChange={(e) => setHasGasInstallation(e.target.checked)}
                                        className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3 text-xs">
                                    <label htmlFor="editHasGasInstallation" className="font-bold text-amber-900">Gas Installation</label>
                                    <p className="text-amber-700">Uncheck if the property is 100% electric.</p>
                                </div>
                            </div>
                        </div>

                        {/* PRS Registration (F-040) / Texas Rental Registration (F-307) */}
                        <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100 mt-4">
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <h4 className="text-sm font-bold text-indigo-900">
                                    {isTexas ? 'Texas Rental Registration' : 'PRS Database Registration'}
                                </h4>
                                {isTexas && (
                                    <button
                                        type="button"
                                        onClick={() => setShowRegWizard(true)}
                                        className="inline-flex items-center text-xs font-bold text-emerald-700 hover:text-emerald-900 whitespace-nowrap"
                                    >
                                        <i className="fas fa-clipboard-check mr-1.5"></i> Texas Registration Wizard
                                    </button>
                                )}
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-indigo-700">
                                        {isTexas ? 'Municipal Registration Number' : 'Registration Number'}
                                    </label>
                                    <input
                                        type="text"
                                        className="mt-1 block w-full border border-indigo-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        value={prsRegNumber}
                                        onChange={e => setPrsRegNumber(e.target.value)}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-indigo-700">Registration Date</label>
                                        <input
                                            type="date"
                                            className="mt-1 block w-full border border-indigo-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            value={prsRegDate}
                                            onChange={e => setPrsRegDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-indigo-700">Expiry Date</label>
                                        <input
                                            type="date"
                                            className="mt-1 block w-full border border-indigo-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            value={prsExpiryDate}
                                            onChange={e => setPrsExpiryDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                            >
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        {/* F-307: kept OUTSIDE the transformed dialog above — a transformed
            ancestor would otherwise become the containing block for the
            wizard's fixed overlay and clip it to the modal. */}
        {isTexas && <PRSWizard isOpen={showRegWizard} onClose={() => setShowRegWizard(false)} />}
        </>
    );
};

export default EditPropertyModal;
